import React, { useEffect } from 'react';
import { Box, Alert, Spinner } from '@cloudscape-design/components';
import { useLocation, useSearchParams } from 'react-router-dom';
import useUserProfile from 'src/hooks/useUserProfile';
import TranslationEditForm from 'src/components/question/translation/TranslationEditForm';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { useQuestion } from 'src/store/question/useQuestion';
import { useUserDetails } from 'src/providers/UserProvider';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { Persona } from 'src/contstant/Persona';
import { useMemo } from 'react';

const TranslationEditPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { closeView } = useSplitPanel();
  const { getUserSelectedLanguage, getPersona } = useUserProfile();

  const { permissions } = useUserDetails();

  const {
    question: loadedQuestion,
    loadQuestionById,
    getQuestionByIdApiStatus,
    clearQuestion,
  } = useQuestion();

  // Check if user has edit permission
  const canEdit = useMemo(() => {
    // Platform admins have all permissions
    if (getPersona() === Persona.PLATFORM_ADMIN) {
      return true;
    }
    // Check if user has the specific permission
    return permissions.has(permissionAccessConfig.QUESTION.EDIT_QUESTION);
  }, [permissions, getPersona]);

  // Get params from URL (primary source of truth)
  const questionId = searchParams.get('questionId');
  const programId = searchParams.get('programId');
  const division = searchParams.get('division');
  const businessUnit = searchParams.get('businessUnit');
  const languageParam = searchParams.get('language');

  // Use navigation state as cache to avoid unnecessary API calls
  const cachedQuestion = location.state?.question;
  const cachedLanguage = location.state?.selectedLanguage;

  // Determine which question and language to use
  // Prefer cached data if it matches the URL params
  const shouldUseCachedQuestion =
    cachedQuestion && cachedQuestion.questionId === questionId;
  const currentQuestion = shouldUseCachedQuestion ? cachedQuestion : loadedQuestion;
  const currentSelectedLanguage =
    cachedLanguage || languageParam || getUserSelectedLanguage();

  useEffect(() => {
    return () => {
      closeView();
    };
  }, [closeView]);

  // Load question if not cached or navigating directly without state
  useEffect(() => {
    if (!shouldUseCachedQuestion && questionId && programId && division && businessUnit) {
      const languageToLoad = languageParam || getUserSelectedLanguage();
      loadQuestionById({
        programId,
        questionId,
        divisionName: division,
        businessUnit,
        language: languageToLoad,
      });
    }
    return () => {
      clearQuestion();
    };
  }, [
    shouldUseCachedQuestion,
    questionId,
    programId,
    division,
    businessUnit,
    languageParam,
    getUserSelectedLanguage,
    loadQuestionById,
    clearQuestion,
  ]);

  // Loading state
  if (getQuestionByIdApiStatus.isLoading) {
    return (
      <Box margin={{ top: 's' }} textAlign="center">
        <Spinner size="large" />
      </Box>
    );
  }

  // Error state - API failure
  if (getQuestionByIdApiStatus.errorMessage) {
    return (
      <Box margin={{ top: 's' }}>
        <Alert type="error" header="Failed to load question">
          {getQuestionByIdApiStatus.errorMessage ||
            'Unable to load question data. Please try again later.'}
        </Alert>
      </Box>
    );
  }

  // Permission denied state
  if (!canEdit) {
    return (
      <Box margin={{ top: 's' }}>
        <Alert type="error" header="Permission denied">
          You do not have permissions to edit question translations.
        </Alert>
      </Box>
    );
  }

  // Question not found state
  if (!currentQuestion) {
    return (
      <Box margin={{ top: 's' }}>
        <Alert type="error" header="Question not found">
          Unable to load question data. Please check the URL and try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box margin={{ top: 's' }}>
      <TranslationEditForm
        question={currentQuestion}
        selectedLanguage={currentSelectedLanguage}
      />
    </Box>
  );
};

export default TranslationEditPage;
